import clsx from 'clsx';
import type { CSSProperties } from 'react';
import { useCallback, useState } from 'react';
import { MdMic, MdMicOff, MdStop } from 'react-icons/md';

import { getServiceId } from '@/api/tokens.helper';
import { useVoiceRecorder, type VoiceRecording } from '@/hooks/useVoiceRecorder';
import { useAuth } from '@/store/auth/auth.store';
import { toast } from '@/utils/toast.ts';
import { TranscribeError, transcribeAudio } from '@/utils/transcribeAudio.ts';

import styles from './VoiceInputButton.module.scss';

export type VoiceInputButtonProps = {
  /** Receives the transcript; the caller decides where it goes (usually the input). */
  onTranscript: (text: string) => void;
  /** Which chat this is — goes to the usage log only. */
  source: string;
  disabled?: boolean;
  /** Attribution for the usage log; default to the signed-in user and the active vessel. */
  userId?: string;
  vesselId?: string;
  /** Lets the host lock its send button while a note is being transcribed. */
  onTranscribingChange?: (transcribing: boolean) => void;
  /**
   * `outlined` matches the AI chat composers (bordered input-style control);
   * `plain` is a bare icon button for toolbars like the crew chat composer.
   */
  appearance?: 'outlined' | 'plain';
  /** Control height in px; width follows (square when idle). */
  size?: number;
  className?: string;
};

const formatElapsed = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const DENIED_HINT = 'Couldn’t use the microphone. Please check your settings.';

const messageFor = (err: unknown) => {
  if (err instanceof TranscribeError) {
    switch (err.info.code) {
      case 'no_speech_detected':
        return 'Couldn’t hear any speech. Please move closer.';
      case 'audio_too_long':
        return 'Please keep voice notes under 2 minutes.';
      case 'model_quota_exhausted':
        return 'Couldn’t start voice input. Please type your message.';
      default:
        return err.info.message;
    }
  }
  return 'Couldn’t transcribe the recording. Please try again.';
};

/**
 * Microphone button for the AI chat composers: tap to record, tap again to stop, and
 * the transcript lands in the input for review — nothing is sent on the user's behalf.
 * Renders nothing where the browser cannot record.
 */
const VoiceInputButton = ({
  onTranscript,
  source,
  disabled = false,
  userId,
  vesselId,
  onTranscribingChange,
  appearance = 'outlined',
  size,
  className,
}: VoiceInputButtonProps) => {
  const sessionUserId = useAuth((state) => state.session?.id);
  const [transcribing, setTranscribing] = useState(false);

  const setBusy = useCallback(
    (value: boolean) => {
      setTranscribing(value);
      onTranscribingChange?.(value);
    },
    [onTranscribingChange],
  );

  const handleRecording = useCallback(
    async (recording: VoiceRecording) => {
      setBusy(true);
      try {
        const result = await transcribeAudio({
          blob: recording.blob,
          extension: recording.extension,
          userId: userId ?? sessionUserId,
          vesselId: vesselId ?? getServiceId(),
          source,
        });
        onTranscript(result.text);
      } catch (err) {
        toast.error(messageFor(err));
      } finally {
        setBusy(false);
      }
    },
    [onTranscript, sessionUserId, setBusy, source, userId, vesselId],
  );

  const { status, elapsedSeconds, maxDurationSeconds, start, stop } = useVoiceRecorder({
    onRecording: handleRecording,
  });

  if (status === 'unsupported') return null;

  const recording = status === 'recording';
  const denied = status === 'denied';
  const busy = transcribing || status === 'requesting';

  const handleClick = () => {
    if (recording) {
      stop();
      return;
    }
    if (denied) {
      toast.error(DENIED_HINT);
    }
    void start();
  };

  const label = recording
    ? `Stop Recording (${formatElapsed(elapsedSeconds)} of ${formatElapsed(maxDurationSeconds)})`
    : transcribing
      ? 'Transcribing…'
      : denied
        ? DENIED_HINT
        : 'Dictate Message';

  return (
    <button
      type="button"
      className={clsx(
        styles.button,
        appearance === 'plain' && styles.plain,
        recording && styles.recording,
        busy && styles.busy,
        className,
      )}
      style={size ? ({ ['--_voice-size' as string]: `${size}px` } as CSSProperties) : undefined}
      onClick={handleClick}
      disabled={disabled || busy}
      aria-label={label}
      aria-pressed={recording}
      title={label}
    >
      {transcribing ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : recording ? (
        <>
          <MdStop aria-hidden="true" />
          <span className={styles.elapsed}>{formatElapsed(elapsedSeconds)}</span>
        </>
      ) : denied ? (
        <MdMicOff aria-hidden="true" />
      ) : (
        <MdMic aria-hidden="true" />
      )}
    </button>
  );
};

export default VoiceInputButton;
